import React, { useState } from 'react';
import {
  ImageIcon,
  FileText,
  Settings,
  CheckCircle2,
  X,
  Palette,
  Eye,
  Users,
  Bot,
  Trash2,
  Trophy as TrophyIcon,
  Save
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { doc, setDoc, getDocs, deleteDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { ref, deleteObject, uploadString, getDownloadURL } from 'firebase/storage';
import { Config, Player, RankingConfig } from '../../types';
import { resizeImage } from '../../lib/utils';
import { logActivity } from '../../services/activityLogger';
import { auth, storage } from '../../lib/firebase';

interface AdminConfigProps {
  config: Config;
  db: any;
  getCollectionPath: (col: string) => string;
  userId?: string;
  players?: Player[];
}

export function AdminConfig({ config, db, getCollectionPath, userId = 'main_club', players = [] }: AdminConfigProps) {
  const [form, setForm] = useState<Config>({
    ...config,
    registrationsEnabled: config.registrationsEnabled !== false,
    pageName: config.pageName || '',
    showPageName: config.showPageName !== false,
    pageNameSize: config.pageNameSize || 'text-2xl',
    logoUrl: config.logoUrl || '',
    logoUrlHorizontal: config.logoUrlHorizontal || '',
    logoShineEffect: config.logoShineEffect || false,
    logoSize: config.logoSize || 'h-12 w-12 md:h-14 md:w-14',
    primaryColor: config.primaryColor || '#eab308',
    secondaryColor: config.secondaryColor || (config as any).accentColor || '#fbbf24',
    bgColor: config.bgColor && config.bgColor.startsWith('#') ? config.bgColor : '#020617',
    cardColor: config.cardColor && config.cardColor.startsWith('#') ? config.cardColor : '#0f172a',
    borderRadius: config.borderRadius || 'xl',
    largeBorderRadius: config.largeBorderRadius || '3xl',
    glowColor: config.glowColor || '#ffffff',
    bgOpacity: config.bgOpacity !== undefined ? config.bgOpacity : 85,
    cardOpacity: config.cardOpacity !== undefined ? config.cardOpacity : 70,
    exportBorderWidth: config.exportBorderWidth || 0,
    exportBorderColor: config.exportBorderColor || '#eab308',
    primaryTextColor: config.primaryTextColor || (config as any).textColor || '#f8fafc',
    secondaryTextColor: config.secondaryTextColor || (config as any).titleColor || '#94a3b8',
    bgUrl: config.bgUrl || '',
    exportWithBg: config.exportWithBg !== false,
    exportBgUrl: config.exportBgUrl || '',
    exportBgColor: config.exportBgColor || '',
    exportBgOpacity: config.exportBgOpacity !== undefined ? config.exportBgOpacity : 85,
    exportCardOpacity: config.exportCardOpacity !== undefined ? config.exportCardOpacity : 70,
    exportShowSponsors: config.exportShowSponsors || false,
    showWelcomeMessage: config.showWelcomeMessage !== false,
    welcomeBanners: config.welcomeBanners || [],
    welcomeBannersDelay: config.welcomeBannersDelay || 5000,
    welcomeCtaUrl: config.welcomeCtaUrl || '',
    fontPrimary: config.fontPrimary || 'system-ui, sans-serif',
    fontSecondary: config.fontSecondary || 'system-ui, sans-serif',
    resultCardSize: config.resultCardSize || 'md',
    bgBlur: config.bgBlur || false,
    bgParallax: config.bgParallax || false,
    bgParallaxIntensity: config.bgParallaxIntensity !== undefined ? config.bgParallaxIntensity : 50,
    footerMode: config.footerMode || 'text',
    footerLogoUrl: config.footerLogoUrl || '',
    footerLogoPosition: config.footerLogoPosition || 'top',
    footerText: config.footerText || '© 2026 Roca Padel Tour. Todos los derechos reservados.',
    footerBannerUrl: config.footerBannerUrl || '',
    autoValidationEnabled: config.autoValidationEnabled || false,
    autoValidationDelay: config.autoValidationDelay || 3,
    autoCategoryValidationEnabled: config.autoCategoryValidationEnabled || false,
    autoCategoryValidationDelay: config.autoCategoryValidationDelay || 3,
    feature_background: config.feature_background !== false,
    feature_welcome: config.feature_welcome !== false,
    feature_identity: config.feature_identity !== false,
    feature_colors: config.feature_colors !== false,
    feature_stats: config.feature_stats !== false,
    feature_medals: config.feature_medals !== false,
    feature_news: config.feature_news !== false,
    feature_cardSize: config.feature_cardSize !== false,
    feature_fonts: config.feature_fonts !== false,
    feature_clubs: config.feature_clubs !== false,
    maintenanceMode: config.maintenanceMode || false,
    adminPin: config.adminPin || '',
    superAdminPin: config.superAdminPin || '',
    superAdminName: config.superAdminName || '',
    superAdminPhotoUrl: config.superAdminPhotoUrl || '',

    autoCleanupCommunity: config.autoCleanupCommunity ?? false,
    autoCleanupDays: config.autoCleanupDays ?? 0,
    autoCleanupHours: config.autoCleanupHours ?? 5,
  });
  const [activeTab, setActiveTab] = useState<'visual' | 'colors' | 'system'>('visual');
  const [msg, setMsg] = useState('');
  

  // Ranking Config State
  const [savingRanking, setSavingRanking] = useState(false);
  const [rankingConfig, setRankingConfig] = useState<RankingConfig>({
    champion: 100,
    runnerUp: 50,
    semifinalist: 25,
    win20: 40,
    win21: 30,
    loss12: 10,
    loss02: 5,
    streakThreshold: 3,
    streakMultiplier: 1.5
  });

  React.useEffect(() => {
    const unsubRanking = onSnapshot(doc(db, getCollectionPath('ranking_config'), 'points_config'), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as Partial<RankingConfig>;
        setRankingConfig({
          champion: data.champion ?? 100,
          runnerUp: data.runnerUp ?? 50,
          semifinalist: data.semifinalist ?? 25,
          thirdPlace: data.thirdPlace ?? 0,
          win20: data.win20 ?? 40,
          win21: data.win21 ?? 30,
          loss12: data.loss12 ?? 10,
          loss02: data.loss02 ?? 5,
          streakThreshold: data.streakThreshold ?? 3,
          streakMultiplier: data.streakMultiplier ?? 1.5,
          awardPointsTo: data.awardPointsTo ?? 'semifinalists'
        } as RankingConfig);
      }
    });
    return () => unsubRanking();
  }, [db, getCollectionPath]);

  const handleSaveRankingConfig = async () => {
    setSavingRanking(true);
    try {
      await setDoc(doc(db, getCollectionPath('ranking_config'), 'points_config'), rankingConfig);
      setMsg('Configuración de ranking guardada correctamente.');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      console.error(err);
      setMsg('Error al guardar la configuración de ranking.');
    } finally {
      setSavingRanking(false);
    }
  };

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingLogoHorizontal, setUploadingLogoHorizontal] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingExportBg, setUploadingExportBg] = useState(false);
  const [uploadingWelcomeBanner, setUploadingWelcomeBanner] = useState(false);
  const [uploadingFooterLogo, setUploadingFooterLogo] = useState(false);
  const [uploadingFooterBanner, setUploadingFooterBanner] = useState(false);
  const [uploadingSuperAdminPhoto, setUploadingSuperAdminPhoto] = useState(false);

  const [presets, setPresets] = useState<any[]>([]);
  const [showPresetsModal, setShowPresetsModal] = useState(false);
  const [presetNameInput, setPresetNameInput] = useState('');
  const [loadingPresets, setLoadingPresets] = useState(false);

  const loadPresets = async () => {
    try {
      setLoadingPresets(true);
      const q = query(collection(db, getCollectionPath('settings')), where('docType', '==', 'preset'));
      const sn = await getDocs(q);
      const loaded = sn.docs.map(d => ({ id: d.id, ...d.data() }));
      setPresets(loaded);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPresets(false);
    }
  };

  const handleSavePreset = async () => {
    if (!presetNameInput.trim()) return alert('Debes darle un nombre al preset');
    const presetId = 'preset_' + Date.now();
    try {
      await setDoc(doc(db, getCollectionPath('settings'), presetId), {
        ...form,
        docType: 'preset',
        presetName: presetNameInput,
        createdAt: Date.now()
      });
      setPresetNameInput('');
      setMsg('Preset guardado correctamente');
      setTimeout(() => setMsg(''), 3000);
      loadPresets();
    } catch (err) {
      alert('Error al guardar preset: ' + (err as any).message);
    }
  };

  const handleDeletePreset = async (id: string) => {
    try {
      await deleteDoc(doc(db, getCollectionPath('settings'), id));
      loadPresets();
    } catch (err) {
      alert('Error eliminando preset');
    }
  };

  const handleApplyPreset = (presetConfig: any) => {
    const { docType, presetName, createdAt, ...rest } = presetConfig;
    setForm({ ...form, ...rest });
    setShowPresetsModal(false);
  };

  const handleSave = async () => {
    try {
      const updatedForm = { ...form };
      const keysToUpload: Array<keyof Config> = ['logoUrl', 'logoUrlHorizontal', 'bgUrl', 'exportBgUrl', 'footerLogoUrl', 'footerBannerUrl', 'superAdminPhotoUrl', 'welcomeCtaUrl'];
        
      let payloadHasImages = false;

      for (const key of keysToUpload) {
        if (typeof updatedForm[key] === 'string' && (updatedForm[key] as string).startsWith('data:image/')) {
          payloadHasImages = true;
          const imageRef = ref(storage, `settings/${key}_${Date.now()}.jpg`);
          await uploadString(imageRef, updatedForm[key] as string, 'data_url');
          updatedForm[key] = await getDownloadURL(imageRef) as any;
        }
      }
        
      if (updatedForm.welcomeBanners) {
        const newBanners = [];
        for (let i = 0; i < updatedForm.welcomeBanners.length; i++) {
          let banner = updatedForm.welcomeBanners[i];
          if (banner.startsWith('data:image/')) {
            payloadHasImages = true;
            const imageRef = ref(storage, `settings/banner_${i}_${Date.now()}.jpg`);
            await uploadString(imageRef, banner, 'data_url');
            banner = await getDownloadURL(imageRef);
          }
          newBanners.push(banner);
        }
        updatedForm.welcomeBanners = newBanners;
      }

      const changes: Partial<Config> = {};
      let hasChanges = false;
  
      (Object.keys(updatedForm) as Array<keyof Config>).forEach((key) => {
        if (JSON.stringify(updatedForm[key]) !== JSON.stringify(config[key])) {
          (changes as any)[key] = updatedForm[key];
          hasChanges = true;
        }
      });
  
      if (!hasChanges) {
        setMsg('No hay cambios nuevos para guardar.');
        setTimeout(() => setMsg(''), 3000);
        return;
      }
  
      // Enviar solo los cambios
      await setDoc(doc(db, getCollectionPath('settings'), 'main'), changes, { merge: true });
      setForm(updatedForm);

      // Registro de Actividad
      const currentUser = auth.currentUser;
      const currentUserProfile = players.find(p => p.id === currentUser?.uid);
      const activeUserName = currentUserProfile?.name || currentUser?.displayName || currentUser?.email || config.pageName || 'Administrador';

      await logActivity(
        db,
        getCollectionPath,
        userId,
        activeUserName,
        'superadmin',
        'Modificación de ajustes globales de configuración'
      );

      setMsg(payloadHasImages ? 'CAMBIOS Y ARCHIVOS GUARDADOS' : 'CAMBIOS GUARDADOS RÁPIDO');
      setTimeout(() => setMsg(''), 3000);
    } catch (error: any) {
      if (error?.message?.includes('exceeds the maximum allowed size')) {
        alert('ERROR DB: La Base de Datos rechazó la subida por exceso de peso bruto (1MB Máximo). Esto es acumulativo. Debes "Limpiar" y borrar alguno de los fondos viejos que no uses para liberar espacio en el documento.');
      } else {
        alert('Error al guardar: ' + error?.message);
      }
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, field: keyof Config, setUploadingState: (v: boolean) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('ADVERTENCIA: La imagen supera el peso máximo de 5MB. Por favor, elige una más ligera para no afectar el rendimiento de la aplicación.');
        return;
      }
      setUploadingState(true);
      // Bajamos la resolución y el quality máximo para evitar saturar BD con base64 gigantes (limite de 1MB en Firestore)
      resizeImage(
        file,
        (compressed) => {
          // El limite duro de Firebase es 1.04MB. Por precaucion no dejamos que ninguna imagen pase de aprox ~400KB.
          if (compressed.length > 500 * 1024) {
            alert('ADVERTENCIA: La imagen comprimida sigue siendo demasiado compleja y pesada para la base de datos en tiempo real. Por favor, sube una imagen más simple o recórtala de antemano.');
            setUploadingState(false);
            return;
          }
          setForm({ ...form, [field]: compressed });
          setUploadingState(false);
        },
        1280, // Max width para ahorrar peso
        1280, // Max height
        0.65  // Compresión más agresiva (estamos guardando string base64 que es caro)
      );
    }
  };

  const handleBannerUpload = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('ADVERTENCIA: La imagen supera el peso máximo de 5MB. Por favor, elige una más ligera o con mejor compresión.');
        return;
      }
      resizeImage(
        file,
        (compressed) => {
          if (compressed.length > 500 * 1024) {
            alert('ADVERTENCIA: La imagen comprimida sigue siendo pesada. Usa una imagen más simple o comprime antes de subirla.');
            return;
          }
          const newBanners = [...(form.welcomeBanners || [])];
          newBanners[index] = compressed;
          setForm({ ...form, welcomeBanners: newBanners });
        },
        1500, // max width para banners (relacion 15:4)
        400, // max height
        0.7
      );
    }
  };

  const removeBanner = (index: number) => {
    const newBanners = [...(form.welcomeBanners || [])];
    newBanners.splice(index, 1);
    setForm({ ...form, welcomeBanners: newBanners });
  };


  const handleDeleteAllTournaments = async () => {
    if (!window.confirm("¡CUIDADO! Esto borrará TODOS los torneos, partidos y equipos de la base de datos de prueba. ¿Estás absolutamente seguro?")) return;
    if (!window.confirm("Última advertencia: ¿Borrar definitivamente TODO?")) return;
    
    try {
      let count = 0;
      const collectionsToClear = ['tournaments', 'matches', 'public_matches', 'teams'];
      
      for (const colName of collectionsToClear) {
        const snapshot = await getDocs(collection(db, getCollectionPath(colName)));
        for (const d of snapshot.docs) {
          await deleteDoc(d.ref);
          count++;
        }
      }
      alert(`¡Destrucción completada! Se eliminaron ${count} documentos (Torneos, Partidos y Equipos).`);
    } catch (e) {
      console.error(e);
      alert("Error al borrar todo.");
    }
  };

  const handleDeleteAllStats = async () => {
    if (!window.confirm("¡CUIDADO! Esto reseteará las estadísticas, rachas y puntos de TODOS los jugadores a cero. ¿Continuar?")) return;
    
    try {
      let count = 0;
      // 1. Delete playerStats collection
      const statsSnap = await getDocs(collection(db, getCollectionPath('playerStats')));
      for (const d of statsSnap.docs) {
        await deleteDoc(d.ref);
      }
      
      // 2. Reset points on players
      const now = Date.now();
      const playersSnap = await getDocs(collection(db, getCollectionPath('players')));
      for (const p of playersSnap.docs) {
        await setDoc(p.ref, {
          points: 0,
          ranking: 0,
          rankingPoints: { global: 0, clubs: {} },
          winStreak: 0,
          isOnFire: false
        }, { merge: true });
        
        await setDoc(doc(collection(db, getCollectionPath('players'), p.id, 'pointsHistory')), {
          points: 0,
          type: 'reset',
          reason: 'Reset Estadísticas por Super Admin',
          createdAt: now
        });
        count++;
      }
      alert(`¡Estadísticas borradas! Se resetearon ${count} jugadores a 0 puntos.`);
    } catch (e) {
      console.error(e);
      alert("Error al resetear estadísticas.");
    }
  };

  const handleResetRankingOnly = async () => {
    if (!window.confirm("¡CUIDADO! Esto reseteará ÚNICAMENTE los puntos de ranking de TODOS los jugadores a cero. Las estadísticas de partidos y rachas se mantendrán intactas. ¿Continuar?")) return;
    
    try {
      let count = 0;
      const now = Date.now();
      const playersSnap = await getDocs(collection(db, getCollectionPath('players')));
      for (const p of playersSnap.docs) {
        await setDoc(p.ref, {
          points: 0,
          ranking: 0,
          rankingPoints: { global: 0, clubs: {} }
        }, { merge: true });

        await setDoc(doc(collection(db, getCollectionPath('players'), p.id, 'pointsHistory')), {
          points: 0,
          type: 'reset',
          reason: 'Reset Ranking por Super Admin',
          createdAt: now
        });
        count++;
      }
      alert(`¡Puntos reseteados! Se devolvieron a 0 los puntos de ${count} jugadores.`);
    } catch (e) {
      console.error(e);
      alert("Error al resetear puntos de ranking.");
    }
  };

  const handleResetAchievements = async () => {
    if (!window.confirm("¡CUIDADO! Esto eliminará TODOS los logros (Trofeos, Super 8, Medallas) de la vitrina de TODOS los jugadores. Sus puntos y partidos quedarán intactos. ¿Continuar?")) return;
    
    try {
      let count = 0;
      const playersSnap = await getDocs(collection(db, getCollectionPath('players')));
      for (const p of playersSnap.docs) {
        await setDoc(p.ref, {
          achievements: []
        }, { merge: true });
        count++;
      }
      alert(`¡Vitrinas vaciadas! Se eliminaron los logros de ${count} jugadores.`);
    } catch (e) {
      console.error(e);
      alert("Error al borrar logros.");
    }
  };

  const tabs = [
    { id: 'visual' as const, label: 'Identidad & Portada', icon: <ImageIcon size={18} />, desc: 'Logos, títulos, banners y footer' },
    { id: 'colors' as const, label: 'Colores & Estilos', icon: <Palette size={18} />, desc: 'Paleta, fondos y opacidades' },
    { id: 'system' as const, label: 'Sistema & Mantenimiento', icon: <Bot size={18} />, desc: 'Optimizaciones y limpieza' },
  ];



  return (
    <div className="space-y-8 animate-in slide-in-from-right-4">

      {/* Selector de Pestañas Premium */}
      <div className="bg-black/40 p-2 rounded-2xl border border-white/10 flex flex-wrap gap-2 shadow-xl">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[160px] flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 relative ${isActive
                ? 'theme-primary-bg text-black shadow-[0_0_15px_rgba(var(--glow-rgb),0.25)] scale-[1.01]'
                : 'bg-transparent text-white/50 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/5'
                }`}
            >
              <div className={`transition-transform duration-300 ${isActive ? 'scale-110' : 'opacity-70'}`}>
                {tab.icon}
              </div>
              <div className="text-left leading-none">
                <span className="block font-black text-[11px]">{tab.label}</span>
                <span className={`text-[8px] block font-normal lowercase opacity-60 mt-0.5 max-w-[180px] truncate ${isActive ? 'text-black/80' : 'text-white/40'}`}>
                  {tab.desc}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Contenido de Pestañas */}
      <div className="space-y-8">

        {/* PESTAÑA: IDENTIDAD & PORTADA */}
        {activeTab === 'visual' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* Identidad Visual */}
            <div className="bg-black/20 p-6 rounded-[var(--radius-large)] border border-white/10">
              <h3 className="text-xl font-black mb-4 uppercase tracking-wider theme-primary-text flex items-center">
                <ImageIcon className="mr-2 text-theme-primary" size={20} /> Identidad Visual
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold opacity-60 uppercase mb-1 block">Nombre de la Página</label>
                    <input
                      type="text"
                      value={form.pageName}
                      onChange={(e) => setForm({ ...form, pageName: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold opacity-60 uppercase mb-1 block">Lema / Subtítulo Oficial</label>
                    <input
                      type="text"
                      value={form.pageSubtitle !== undefined ? form.pageSubtitle : 'Official Platform'}
                      onChange={(e) => setForm({ ...form, pageSubtitle: e.target.value })}
                      placeholder="Ej: Official Platform"
                      className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold opacity-60 uppercase mb-1 block">Tipografía Títulos</label>
                      <input
                        type="text"
                        value={form.fontPrimary}
                        onChange={(e) => setForm({ ...form, fontPrimary: e.target.value })}
                        placeholder="Ej: Outfit, Inter"
                        className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border font-mono text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold opacity-60 uppercase mb-1 block">Tipografía Textos</label>
                      <input
                        type="text"
                        value={form.fontSecondary}
                        onChange={(e) => setForm({ ...form, fontSecondary: e.target.value })}
                        placeholder="Ej: Roboto, system-ui"
                        className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border font-mono text-xs"
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer bg-black/40 px-4 py-2 rounded-xl border border-white/5">
                      <input
                        type="checkbox"
                        checked={form.showPageName}
                        onChange={(e) => setForm({ ...form, showPageName: e.target.checked })}
                        className="w-4 h-4"
                        style={{ accentColor: form.primaryColor || '#eab308' }}
                      />
                      <span className="text-xs font-bold">Mostrar Nombre junto al Logo</span>
                    </label>
                  </div>
                </div>

                <div className="md:col-span-2 space-y-4">
                  <label className="text-xs font-bold opacity-60 uppercase mb-2 block">Logos de la Plataforma</label>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Logo Cuadrado - Móvil */}
                    <div className="bg-black/40 p-4 rounded-2xl border border-white/5 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center text-[9px] font-black opacity-60">1:1</div>
                        <span className="text-[10px] font-black uppercase tracking-widest opacity-70">Logo Cuadrado</span>
                        <span className="ml-auto text-[8px] font-bold bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full uppercase tracking-wider">Móvil</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 bg-black/40 rounded-xl border border-white/10 flex items-center justify-center relative overflow-hidden group shrink-0">
                          {form.logoUrl ? (
                            <img src={form.logoUrl} className="w-full h-full object-contain p-1.5" alt="logo cuadrado" />
                          ) : (
                            <ImageIcon className="opacity-20 w-7 h-7" />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'logoUrl', setUploadingLogo)}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                          />
                          {uploadingLogo && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <span className="text-[9px] animate-pulse">...</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="flex gap-1.5">
                            {['S', 'M', 'L'].map(size => {
                              const sizeClass = size === 'S' ? 'h-8 w-8 md:h-10 md:w-10' : size === 'L' ? 'h-12 w-12 md:h-16 md:w-16' : 'h-10 w-10 md:h-12 md:w-12';
                              return (
                                <button
                                  key={size}
                                  onClick={() => setForm({ ...form, logoSize: sizeClass })}
                                  className={`px-3 py-1 text-[10px] font-bold rounded border uppercase ${form.logoSize === sizeClass ? 'theme-primary-bg text-black border-transparent' : 'bg-black/40 border-white/10 hover:border-white/30 text-white/70'}`}
                                >
                                  {size}
                                </button>
                              );
                            })}
                          </div>
                          {form.logoUrl && (
                            <button
                              onClick={() => setForm({ ...form, logoUrl: '' })}
                              className="text-[10px] text-red-400 hover:bg-red-500/10 px-2 py-1 rounded-lg transition-colors border border-red-500/20 font-bold block"
                            >
                              Quitar
                            </button>
                          )}
                          <div className="text-[9px] opacity-50 leading-tight">Recomendado: 1:1.<br />Max 5MB.</div>
                        </div>
                      </div>
                    </div>

                    {/* Logo Horizontal - Escritorio */}
                    <div className="bg-black/40 p-4 rounded-2xl border border-white/5 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-7 h-4 rounded-sm bg-white/10 flex items-center justify-center text-[7px] font-black opacity-60">3:1</div>
                        <span className="text-[10px] font-black uppercase tracking-widest opacity-70">Logo Horizontal</span>
                        <span className="ml-auto text-[8px] font-bold bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full uppercase tracking-wider">Escritorio</span>
                      </div>
                      <div className="w-full h-16 bg-black/40 rounded-xl border border-white/10 flex items-center justify-center relative overflow-hidden group">
                        {form.logoUrlHorizontal ? (
                          <img src={form.logoUrlHorizontal} className="w-full h-full object-contain p-2" alt="logo horizontal" />
                        ) : (
                          <div className="flex flex-col items-center opacity-30 gap-1">
                            <ImageIcon className="w-5 h-5" />
                            <span className="text-[8px] uppercase tracking-widest font-bold">Sin logo horizontal</span>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'logoUrlHorizontal' as any, setUploadingLogoHorizontal)}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                        {uploadingLogoHorizontal && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="text-[9px] animate-pulse">...</span>
                          </div>
                        )}
                        <span className="absolute text-[9px] font-bold opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 px-3 py-1.5 rounded-lg border border-white/10">
                          {form.logoUrlHorizontal ? 'Cambiar' : 'Subir logo horizontal'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-[9px] opacity-50 leading-tight">Recomendado: 3:1 o 4:1.<br />Max 5MB. PNG con fondo transparente.</div>
                        {form.logoUrlHorizontal && (
                          <button
                            onClick={() => setForm({ ...form, logoUrlHorizontal: '' })}
                            className="text-[10px] text-red-400 hover:bg-red-500/10 px-2 py-1 rounded-lg transition-colors border border-red-500/20 font-bold shrink-0"
                          >
                            Quitar
                          </button>
                        )}
                      </div>
                      <p className="text-[9px] opacity-40 bg-black/30 p-2 rounded-lg border border-white/5 leading-relaxed">
                        Si se sube, reemplaza al logo cuadrado en pantallas de escritorio (<span className="font-bold">md:</span> y superiores).
                      </p>
                    </div>
                  </div>

                  {/* Toggle: Efecto Metálico */}
                  <div
                    onClick={() => setForm({ ...form, logoShineEffect: !form.logoShineEffect })}
                    className={`flex items-center justify-between gap-4 p-3 rounded-xl border cursor-pointer transition-all duration-300 select-none ${
                      form.logoShineEffect
                        ? 'bg-white/5 border-white/20 shadow-[0_0_12px_rgba(255,255,255,0.06)]'
                        : 'bg-black/30 border-white/5 hover:border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base transition-all ${
                        form.logoShineEffect ? 'bg-white/15 shadow-inner' : 'bg-white/5'
                      }`}>
                        ✨
                      </div>
                      <div>
                        <p className="text-[11px] font-black uppercase tracking-widest">
                          Efecto Metálico de Barrido
                        </p>
                        <p className="text-[9px] opacity-50 mt-0.5">
                          Animación de reflejo que recorre el logo periódicamente
                        </p>
                      </div>
                    </div>
                    <div className={`w-11 h-6 rounded-full relative transition-all duration-300 shrink-0 ${
                      form.logoShineEffect ? 'theme-primary-bg shadow-[0_0_10px_rgba(var(--glow-rgb),0.4)]' : 'bg-white/10'
                    }`}>
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-md transition-all duration-300 ${
                        form.logoShineEffect ? 'left-6' : 'left-1'
                      }`} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Perfil del Super Admin */}
            <div className="bg-black/20 p-6 rounded-[var(--radius-large)] border border-white/10">
              <h3 className="text-xl font-black mb-4 uppercase tracking-wider theme-primary-text flex items-center">
                🛡️ Perfil del Super Admin
              </h3>
              <p className="text-[10px] text-white/40 mb-4 uppercase tracking-wider font-bold">
                Personaliza la identidad con la que interactúas, chateas, respondes consultas de soporte y publicas en la comunidad cuando actúas en Modo Administrador.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold opacity-60 uppercase mb-1 block">Nombre Visible</label>
                    <input
                      type="text"
                      value={form.superAdminName || ''}
                      onChange={(e) => setForm({ ...form, superAdminName: e.target.value })}
                      placeholder="Ej: Super Admin"
                      className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none focus:theme-primary-border"
                    />
                  </div>
                  <div className="text-[10px] opacity-40 bg-black/30 p-3 rounded-lg border border-white/5 leading-relaxed">
                    Si se deja vacío, el sistema utilizará de manera predeterminada el nombre genérico <b>"Super Admin"</b>.
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-xs font-bold opacity-60 uppercase block">Avatar de Super Admin</label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-black/40 rounded-full border border-white/10 flex items-center justify-center relative overflow-hidden group shrink-0">
                      {form.superAdminPhotoUrl ? (
                        <img src={form.superAdminPhotoUrl} className="w-full h-full object-cover" alt="super admin avatar" />
                      ) : form.logoUrl ? (
                        <img src={form.logoUrl} className="w-full h-full object-contain p-2 opacity-50" alt="default admin logo" />
                      ) : (
                        <ImageIcon className="opacity-20 w-8 h-8" />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'superAdminPhotoUrl' as keyof Config, setUploadingSuperAdminPhoto)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      {uploadingSuperAdminPhoto && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-[9px] animate-pulse text-white">...</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="text-[10px] opacity-50 leading-tight">
                        Sube una foto cuadrada para el avatar de tu perfil administrativo.
                      </div>
                      <div className="flex gap-2">
                        {form.superAdminPhotoUrl && (
                          <button
                            onClick={() => setForm({ ...form, superAdminPhotoUrl: '' })}
                            className="text-[10px] text-red-400 hover:bg-red-500/10 px-2 py-1 rounded-lg transition-colors border border-red-500/20 font-bold"
                          >
                            Restablecer al Logo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Landing de Invitados */}
            <div className="bg-black/20 p-6 rounded-[var(--radius-large)] border border-white/10">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-black uppercase tracking-wider theme-primary-text flex items-center">
                  <Users className="mr-2 text-theme-primary" size={20} /> Landing de Invitados
                </h3>
              </div>
              <p className="text-[10px] text-white/40 mb-4 uppercase tracking-wider font-bold">
                Configura lo que ven los usuarios que aún no se registraron al entrar a la página.
              </p>

              <div className="space-y-4">


                {/* Textos del Hero */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold opacity-60 uppercase mb-1.5 block">Título principal</label>
                    <input
                      type="text"
                      value={(form as any).guestHeroTitle || ''}
                      onChange={(e) => setForm({ ...form, guestHeroTitle: e.target.value } as any)}
                      placeholder="Únete a la"
                      className="w-full px-3 py-2.5 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold opacity-60 uppercase mb-1.5 block">Palabra destacada (color primario)</label>
                    <input
                      type="text"
                      value={(form as any).guestHeroTitleAccent || ''}
                      onChange={(e) => setForm({ ...form, guestHeroTitleAccent: e.target.value } as any)}
                      placeholder="Élite"
                      className="w-full px-3 py-2.5 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold opacity-60 uppercase mb-1.5 block">Subtítulo</label>
                    <input
                      type="text"
                      value={(form as any).guestHeroSubtitle || ''}
                      onChange={(e) => setForm({ ...form, guestHeroSubtitle: e.target.value } as any)}
                      placeholder="La plataforma donde se juegan los mejores partidos..."
                      className="w-full px-3 py-2.5 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30"
                    />
                  </div>
                </div>


              </div>
            </div>

            {/* Portada & Bienvenida */}
            <div className="bg-black/20 p-6 rounded-[var(--radius-large)] border border-white/10">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-black uppercase tracking-wider theme-primary-text flex items-center">
                  <FileText className="mr-2 text-theme-primary" size={20} /> Portada & Bienvenida
                </h3>
                <label className="flex items-center gap-2 cursor-pointer bg-black/40 px-3 py-1.5 rounded-xl border border-white/5 hover:border-white/20 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.showWelcomeMessage}
                    onChange={(e) => setForm({ ...form, showWelcomeMessage: e.target.checked })}
                    className="w-4 h-4"
                    style={{ accentColor: form.primaryColor || '#eab308' }}
                  />
                  <span className="text-xs font-bold uppercase">Mostrar</span>
                </label>
              </div>

              {form.showWelcomeMessage && (
                <div className="animate-in slide-in-from-top-2 pt-4">
                  <div className="text-xs font-bold opacity-60 uppercase mb-4">
                    Carrusel de Banners <span className="text-xs font-normal lowercase opacity-80">(Máx 4)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[0, 1, 2, 3].map((index) => {
                      const currentBanner = form.welcomeBanners?.[index];
                      return (
                        <div key={index} className="relative h-32 bg-black/40 rounded-xl border border-white/10 overflow-hidden flex items-center justify-center group shrink-0">
                          {currentBanner ? (
                            <>
                              <img src={currentBanner} className="w-full h-full object-cover opacity-90 group-hover:opacity-30 transition-opacity" alt={`banner ${index + 1}`} />
                              <button
                                onClick={(e) => { e.preventDefault(); removeBanner(index); }}
                                className="absolute z-20 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 text-white p-2 rounded-full shadow-lg hover:scale-110 active:scale-95"
                              >
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <div className="flex flex-col items-center opacity-40 group-hover:opacity-100 transition-opacity">
                              <ImageIcon className="mb-2" size={24} />
                              <span className="text-[10px] font-bold uppercase tracking-widest px-4 text-center">Añadir Banner {index + 1}</span>
                            </div>
                          )}

                          {!currentBanner && (
                            <input
                              type="file"
                              accept="image/png, image/webp, image/jpeg"
                              onChange={(e) => handleBannerUpload(e, index)}
                              className="absolute inset-0 opacity-0 cursor-pointer z-10"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] opacity-60 leading-tight mt-4 bg-black/40 p-3 rounded-xl border border-white/5">
                    Recomendado: <b>1500x400px (relación de aspecto 15:4)</b> para la correcta visualización en dispositivos móviles y escritorio. Peso máximo por foto: 5MB.
                  </p>

                  <div className="mt-6">
                    <label className="text-xs font-bold opacity-60 uppercase mb-2 block">Tiempo de rotación (segundos)</label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="2"
                        max="15"
                        step="1"
                        value={(form.welcomeBannersDelay || 5000) / 1000}
                        onChange={(e) => setForm({ ...form, welcomeBannersDelay: parseInt(e.target.value) * 1000 })}
                        className="w-full sm:w-64 accent-[#eab308]"
                        style={{ accentColor: form.primaryColor || '#eab308' }}
                      />
                      <span className="text-sm font-black w-16">{(form.welcomeBannersDelay || 5000) / 1000}s</span>
                    </div>
                    <p className="text-[10px] opacity-40 mt-2">Ajusta cuánto tiempo se mostrará cada imagen antes de pasar a la siguiente.</p>
                  </div>

                  <div className="mt-6 border-t border-white/5 pt-6">
                    <label className="text-xs font-bold opacity-60 uppercase mb-2 block">Botón Interactivo de Invitación (CTA)</label>
                    <p className="text-[10px] opacity-60 mb-4">Sube un botón u hoja horizontal personalizada de "inscribirse". Unicamente será mostrada a los visitantes que aun NO están logueados.</p>
                    <div className="flex items-center gap-4">
                      <div className="w-48 h-16 bg-black/40 rounded-xl border border-white/10 flex items-center justify-center relative overflow-hidden group">
                        {form.welcomeCtaUrl ? (
                          <img src={form.welcomeCtaUrl} className="w-full h-full object-cover p-1" alt="CTA Button" />
                        ) : (
                          <ImageIcon className="opacity-20 w-6 h-6" />
                        )}
                        <input
                          type="file"
                          accept="image/png, image/webp, image/jpeg"
                          onChange={(e) => handleImageUpload(e, 'welcomeCtaUrl' as keyof Config, setUploadingWelcomeBanner)}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                        {uploadingWelcomeBanner && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-[10px] animate-pulse">...</span>
                          </div>
                        )}
                      </div>
                      {form.welcomeCtaUrl && (
                        <button
                          onClick={() => setForm({ ...form, welcomeCtaUrl: '' })}
                          className="text-xs font-bold text-red-400 opacity-60 hover:opacity-100 uppercase tracking-widest"
                        >
                          Quitar textura
                        </button>
                      )}
                    </div>
                    <p className="text-[10px] opacity-40 mt-3">Recomendado: 1000x250px (relación 4:1). Usa .png para mantener bordes transparentes.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Pie de Página / Footer */}
            <div className="bg-black/20 p-6 rounded-[var(--radius-large)] border border-white/10">
              <h3 className="text-xl font-black uppercase tracking-wider theme-primary-text flex items-center mb-6">
                <FileText className="mr-2 text-theme-primary" size={20} /> Pie de Página (Footer)
              </h3>

              <div className="animate-in slide-in-from-top-2">
                <div className="flex gap-2 bg-black/50 p-1.5 rounded-lg border border-white/10 mb-6">
                  <button
                    onClick={() => setForm({ ...form, footerMode: 'text' })}
                    className={`flex-1 py-2 rounded text-xs font-bold uppercase transition-all ${form.footerMode !== 'image' ? 'bg-white text-black' : 'text-white/50 hover:bg-white/10'}`}
                  >
                    Logo + Texto
                  </button>
                  <button
                    onClick={() => setForm({ ...form, footerMode: 'image' })}
                    className={`flex-1 py-2 rounded text-xs font-bold uppercase transition-all ${form.footerMode === 'image' ? 'bg-white text-black' : 'text-white/50 hover:bg-white/10'}`}
                  >
                    Banner Imagen
                  </button>
                </div>

                {form.footerMode === 'image' ? (
                  <div className="space-y-4 animate-in fade-in">
                    <div className="relative h-24 bg-black/40 rounded-xl border border-white/10 overflow-hidden flex items-center justify-center group w-full max-w-lg mx-auto">
                      {form.footerBannerUrl ? (
                        <img src={form.footerBannerUrl} className="w-full h-full object-contain opacity-90 group-hover:opacity-30 transition-opacity" alt="footer banner" />
                      ) : (
                        <div className="text-[10px] opacity-40 uppercase tracking-widest px-4 text-center"><ImageIcon className="mx-auto mb-1 opacity-50 w-6 h-6" />Subir Banner Footer (PNG/WebP)</div>
                      )}
                      <input
                        type="file"
                        accept="image/png, image/webp"
                        onChange={(e) => handleImageUpload(e, 'footerBannerUrl', setUploadingFooterBanner)}
                        className="absolute inset-0 opacity-0 cursor-pointer z-10"
                      />
                      <div className="absolute pointer-events-none flex flex-col items-center">
                        {uploadingFooterBanner ? (
                          <span className="text-xs font-bold animate-pulse shadow-lg bg-black/80 px-2 rounded">Subiendo...</span>
                        ) : (
                          <span className="text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 px-3 py-1.5 rounded-lg border border-white/10 shadow-lg">
                            Cambiar Banner Footer
                          </span>
                        )}
                      </div>
                    </div>
                    {form.footerBannerUrl && (
                      <button onClick={() => setForm({ ...form, footerBannerUrl: '' })} className="text-[10px] text-red-400 font-bold uppercase hover:underline text-center w-full block">
                        Quitar Banner
                      </button>
                    )}
                    <div className="text-[10px] opacity-60 leading-tight text-center">
                      Sube un banner transparente. Se visualizará centrado al fondo de la plataforma. Max: 5MB.
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 bg-black/40 rounded-2xl border border-white/10 flex items-center justify-center relative overflow-hidden group shrink-0">
                        {form.footerLogoUrl ? (
                          <img src={form.footerLogoUrl} className="w-full h-full object-contain p-2" alt="footer logo" />
                        ) : (
                          <ImageIcon className="opacity-20 w-8 h-8" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'footerLogoUrl', setUploadingFooterLogo)}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                        {uploadingFooterLogo && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-[10px] animate-pulse">...</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[10px] font-bold opacity-60 uppercase">Logo Pequeño (Opcional)</label>
                        {form.footerLogoUrl && (
                          <button onClick={() => setForm({ ...form, footerLogoUrl: '' })} className="text-[10px] text-red-400 font-bold uppercase hover:underline text-left">
                            Limpiar Logo
                          </button>
                        )}
                      </div>
                    </div>

                    {form.footerLogoUrl && (
                      <div className="md:col-span-2 bg-black/40 p-4 rounded-xl border border-white/5">
                        <label className="text-[10px] font-bold opacity-60 uppercase mb-3 block">Posición del logo (Respecto al texto)</label>
                        <div className="flex flex-wrap gap-2">
                          {['top', 'bottom', 'left', 'right'].map(pos => (
                            <button
                              key={pos}
                              onClick={() => setForm({ ...form, footerLogoPosition: pos as any })}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase transition-colors border ${form.footerLogoPosition === pos ? 'bg-white text-black border-white' : 'bg-transparent text-white/50 border-white/10 hover:border-white/30'}`}
                            >
                              {pos === 'top' ? 'Arriba' : pos === 'bottom' ? 'Abajo' : pos === 'left' ? 'Izquierda' : 'Derecha'}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className={`${form.footerLogoUrl ? 'md:col-span-2' : ''}`}>
                      <label className="text-xs font-bold opacity-60 uppercase mb-1 block">Texto del Pie de Página</label>
                      <textarea
                        value={form.footerText}
                        onChange={(e) => setForm({ ...form, footerText: e.target.value })}
                        className="w-full bg-black/40 border border-white/10 rounded-xl p-3 outline-none h-20 focus:theme-primary-border"
                      />
                      <div className="text-[10px] opacity-60 mt-1">Este texto aparecerá centrado en pequeño junto al logo si es que subiste uno.</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PESTAÑA: COLORES & ESTILOS UI */}
        {activeTab === 'colors' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* Paleta de Colores */}
            <div className="bg-black/20 p-6 rounded-[var(--radius-large)] border border-white/10">
              <h3 className="text-xl font-black mb-6 uppercase tracking-wider theme-primary-text flex items-center">
                <Palette className="mr-2 text-theme-primary" size={20} /> Personalización de Colores
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { label: 'Color Principal', field: 'primaryColor' as keyof Config, desc: 'Botones y destacados' },
                  { label: 'Color Secundario', field: 'secondaryColor' as keyof Config, desc: 'Detalles alternativos' },
                  { label: 'Color de Textos Primarios', field: 'primaryTextColor' as keyof Config, desc: 'Títulos y párrafos' },
                  { label: 'Color de Textos Secundarios', field: 'secondaryTextColor' as keyof Config, desc: 'Textos tenues e info' },
                  { label: 'Fondo Base (App)', field: 'bgColor' as keyof Config, desc: 'Sólido color del fondo' },
                  { label: 'Fondo de Tarjetas', field: 'cardColor' as keyof Config, desc: 'Listados y modales' },
                  { label: 'Resplandor (Glow)', field: 'glowColor' as keyof Config, desc: 'Bordes y sombras luminosas' },
                ].map((item) => (
                  <div key={item.field} className="flex flex-col gap-2 bg-black/40 p-4 rounded-2xl border border-white/5">
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest">{item.label}</label>
                      <p className="text-[9px] opacity-50 uppercase">{item.desc}</p>
                    </div>
                    <div className="flex gap-2 items-center mt-1">
                      <input
                        type="color"
                        value={(form as any)[item.field]}
                        onChange={(e) => setForm({ ...form, [item.field]: e.target.value })}
                        className="w-10 h-10 rounded-xl border-0 bg-transparent cursor-pointer shrink-0"
                      />
                      <input
                        type="text"
                        value={(form as any)[item.field]}
                        onChange={(e) => setForm({ ...form, [item.field]: e.target.value })}
                        className="flex-1 bg-black/50 border border-white/10 rounded-xl p-2.5 text-xs font-mono uppercase focus:theme-primary-border outline-none transition-all"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 pt-6 border-t border-white/10">
                <div>
                  <label className="text-[10px] font-black opacity-60 uppercase mb-2 block">Redondeado de Bordes</label>
                  <select
                    value={form.borderRadius}
                    onChange={(e) => setForm({ ...form, borderRadius: e.target.value as any })}
                    className="w-full bg-black/50 border border-white/10 rounded-xl p-3 text-xs font-bold outline-none focus:theme-primary-border transition-all appearance-none"
                  >
                    <option value="none">Sin redondeado (Cuadrado)</option>
                    <option value="sm">Pequeño (sm)</option>
                    <option value="md">Medio (md)</option>
                    <option value="lg">Grande (lg)</option>
                    <option value="xl">Extra Grande (xl - Default)</option>
                    <option value="2xl">2X Grande (2xl)</option>
                    <option value="3xl">3X Grande (3xl)</option>
                    <option value="full">Completamente Redondo (full)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black opacity-60 uppercase mb-2 block">Redondeado Ventanas Grandes</label>
                  <select
                    value={form.largeBorderRadius}
                    onChange={(e) => setForm({ ...form, largeBorderRadius: e.target.value as any })}
                    className="w-full bg-black/50 border border-white/10 rounded-xl p-3 text-xs font-bold outline-none focus:theme-primary-border transition-all appearance-none"
                  >
                    <option value="none">Sin redondeado (Cuadrado)</option>
                    <option value="sm">Pequeño (sm)</option>
                    <option value="md">Medio (md)</option>
                    <option value="lg">Grande (lg)</option>
                    <option value="xl">Extra Grande (xl)</option>
                    <option value="2xl">2X Grande (2xl)</option>
                    <option value="3xl">3X Grande (3xl - Default)</option>
                    <option value="4xl">4X Grande (4xl)</option>
                    <option value="full">Completamente Redondo (full)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black opacity-60 uppercase mb-2 block">Opacidad del Fondo Base ({form.bgOpacity}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={form.bgOpacity}
                    onChange={(e) => setForm({ ...form, bgOpacity: Number(e.target.value) })}
                    className="w-full mt-2"
                    style={{ accentColor: form.primaryColor || '#eab308' }}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black opacity-60 uppercase mb-2 block">Opacidad de Tarjetas ({form.cardOpacity}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={form.cardOpacity}
                    onChange={(e) => setForm({ ...form, cardOpacity: Number(e.target.value) })}
                    className="w-full mt-2"
                    style={{ accentColor: form.primaryColor || '#eab308' }}
                  />
                </div>
              </div>
            </div>

            {/* Cabecera y Fondos */}
            <div className="bg-black/20 p-6 rounded-[var(--radius-large)] border border-white/10 space-y-8">
              <h3 className="text-xl font-black uppercase tracking-wider theme-primary-text flex items-center">
                <ImageIcon className="mr-2 text-theme-primary" size={20} /> Cabecera y Fondos Principales
              </h3>

              <div className="grid grid-cols-1 gap-8">

                {/* Fondo App */}
                <div className="space-y-4 bg-black/40 p-4 rounded-2xl border border-white/5">
                  <label className="text-sm font-bold block uppercase tracking-widest opacity-80 border-b border-white/10 pb-2">
                    Fondo General de la App
                  </label>

                  <div className="flex gap-2 bg-black/50 p-1.5 rounded-lg border border-white/10">
                    <button
                      onClick={() => setForm({ ...form, bgType: 'color' })}
                      className={`flex-1 py-2 rounded text-xs font-bold uppercase transition-all ${form.bgType === 'color' ? 'bg-white text-black' : 'text-white/50 hover:bg-white/10'}`}
                    >
                      Color Sólido
                    </button>
                    <button
                      onClick={() => setForm({ ...form, bgType: 'image' })}
                      className={`flex-1 py-2 rounded text-xs font-bold uppercase transition-all ${form.bgType !== 'color' ? 'bg-white text-black' : 'text-white/50 hover:bg-white/10'}`}
                    >
                      Imagen de Fondo
                    </button>
                  </div>

                  {form.bgType === 'color' ? (
                    <div className="text-[10px] opacity-60 uppercase bg-black/20 p-3 rounded-lg border border-white/5">
                      Utilizará tu "Fondo Base (App)" configurado en la paleta de Colores.
                    </div>
                  ) : (
                    <div className="animate-in fade-in space-y-2">
                      <div className="relative h-32 bg-black/40 rounded-xl border border-white/10 overflow-hidden flex items-center justify-center group">
                        {form.bgUrl ? (
                          <img src={form.bgUrl} className="w-full h-full object-cover opacity-50 group-hover:opacity-30 transition-opacity" alt="bg" />
                        ) : (
                          <div className="text-[10px] opacity-40 uppercase tracking-widest px-4 text-center"><ImageIcon className="mx-auto mb-1 opacity-50 w-6 h-6" />Subir Imagen Base</div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'bgUrl', setUploadingBg)}
                          className="absolute inset-0 opacity-0 cursor-pointer z-10"
                        />
                        <div className="absolute pointer-events-none flex flex-col items-center">
                          {uploadingBg ? (
                            <span className="text-xs font-bold animate-pulse shadow-lg bg-black/80 px-2 rounded">Subiendo...</span>
                          ) : (
                            <span className="text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 px-3 py-1.5 rounded-lg border border-white/10 shadow-lg">
                              Elegir Fondo
                            </span>
                          )}
                        </div>
                      </div>
                      {form.bgUrl && (
                        <button onClick={() => setForm({ ...form, bgUrl: '' })} className="text-[10px] text-red-400 font-bold uppercase hover:underline mb-2 block">
                          Limpiar Fondo App
                        </button>
                      )}
                      <div className="flex flex-col gap-3">
                        <div className="flex gap-4 items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={form.bgBlur}
                              onChange={(e) => setForm({ ...form, bgBlur: e.target.checked })}
                              className="w-4 h-4"
                              style={{ accentColor: form.primaryColor || '#eab308' }}
                            />
                            <span className="text-xs font-bold uppercase">Desenfocar Fondo</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={form.bgParallax}
                              onChange={(e) => setForm({ ...form, bgParallax: e.target.checked })}
                              className="w-4 h-4"
                              style={{ accentColor: form.primaryColor || '#eab308' }}
                            />
                            <span className="text-xs font-bold uppercase">Efecto Parallax</span>
                          </label>
                        </div>
                        {form.bgParallax && (
                          <div className="animate-in fade-in bg-black/20 p-2 rounded-lg border border-white/5">
                            <label className="text-[10px] font-black opacity-60 uppercase flex justify-between mb-2">
                              <span>Intensidad del Parallax</span>
                              <span>{form.bgParallaxIntensity || 50}%</span>
                            </label>
                            <input
                              type="range"
                              min="10"
                              max="100"
                              value={form.bgParallaxIntensity || 50}
                              onChange={(e) => setForm({ ...form, bgParallaxIntensity: Number(e.target.value) })}
                              className="w-full"
                              style={{ accentColor: form.primaryColor || '#eab308' }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="text-[10px] opacity-60 leading-tight mt-2">
                        Recomendado: 16:9 ó 9:16. Peso máximo: 5MB.<br />Se aplicarán sombras dinámicas automáticamente.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-in fade-in duration-300 items-start">
            {/* Parámetros Globales de Puntuación */}
            <div className="bg-gradient-to-br from-amber-950/40 to-black/60 p-4 rounded-xl border border-amber-500/20 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-[0.03] pointer-events-none">
                <TrophyIcon size={80} />
              </div>
              
              <div className="flex items-center gap-2 mb-3 relative z-10 border-b border-white/10 pb-2">
                <div className="p-2 bg-theme-primary/10 rounded-xl theme-primary-text">
                  <TrophyIcon size={16} />
                </div>
                <div>
                  <h4 className="text-sm font-black uppercase tracking-wider theme-primary-text">Parámetros Globales de Puntuación</h4>
                  <p className="opacity-60 text-white text-[10px] uppercase tracking-wider mt-1 font-bold">
                    Define cuántos puntos se otorgan por logros en torneos.
                  </p>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSaveRankingConfig(); }} className="space-y-3 relative z-10">
                {/* Puntos Fijos por Puesto */}
                <div className="flex flex-col 2xl:flex-row gap-3">
                  <div className="flex-1 bg-black/40 p-3 rounded-lg border border-white/5">
                    <span className="text-[9px] font-black uppercase tracking-widest text-amber-400 block text-center w-full mb-2">Por Puesto</span>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="w-full text-center">
                        <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">Campeón</label>
                        <input
                          type="number"
                          value={rankingConfig.champion}
                          onChange={(e) => setRankingConfig({...rankingConfig, champion: parseInt(e.target.value) || 0})}
                          className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-1 py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                        />
                      </div>
                      <div className="w-full text-center">
                        <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">Finalista</label>
                        <input
                          type="number"
                          value={rankingConfig.runnerUp}
                          onChange={(e) => setRankingConfig({...rankingConfig, runnerUp: parseInt(e.target.value) || 0})}
                          className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-1 py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                        />
                      </div>
                      {rankingConfig.awardPointsTo === 'third_place' ? (
                        <div className="w-full text-center">
                          <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">3er Puesto</label>
                          <input
                            type="number"
                            value={rankingConfig.thirdPlace}
                            onChange={(e) => setRankingConfig({...rankingConfig, thirdPlace: parseInt(e.target.value) || 0})}
                            className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-1 py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                          />
                        </div>
                      ) : (
                        <div className="w-full text-center">
                          <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">Semi</label>
                          <input
                            type="number"
                            value={rankingConfig.semifinalist}
                            onChange={(e) => setRankingConfig({...rankingConfig, semifinalist: parseInt(e.target.value) || 0})}
                            className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-1 py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                          />
                        </div>
                      )}
                    </div>
                    <div className="mt-3 border-t border-white/10 pt-2">
                      <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">Modo de Reparto (Semi / 3ero)</label>
                      <select
                        value={rankingConfig.awardPointsTo || 'semifinalists'}
                        onChange={(e) => setRankingConfig({...rankingConfig, awardPointsTo: e.target.value as 'semifinalists' | 'third_place'})}
                        className="w-full bg-white/10 border border-white/15 rounded-lg px-2 py-1.5 text-xs text-white outline-none focus:border-amber-500"
                      >
                        <option value="semifinalists">A todos los Semifinalistas</option>
                        <option value="third_place">Solo al 3er Puesto (Requiere jugar)</option>
                      </select>
                    </div>
                  </div>

                  {/* Puntos por Partido */}
                  <div className="flex-1 bg-black/40 p-3 rounded-lg border border-white/5">
                    <span className="text-[9px] font-black uppercase tracking-widest text-amber-400 block text-center w-full mb-2">Por Partido (Sets)</span>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="w-full text-center">
                        <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">G: 2-0</label>
                        <input
                          type="number"
                          value={rankingConfig.win20}
                          onChange={(e) => setRankingConfig({...rankingConfig, win20: parseInt(e.target.value) || 0})}
                          className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                        />
                      </div>
                      <div className="w-full text-center">
                        <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">G: 2-1</label>
                        <input
                          type="number"
                          value={rankingConfig.win21}
                          onChange={(e) => setRankingConfig({...rankingConfig, win21: parseInt(e.target.value) || 0})}
                          className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                        />
                      </div>
                      <div className="w-full text-center">
                        <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">P: 1-2</label>
                        <input
                          type="number"
                          value={rankingConfig.loss12}
                          onChange={(e) => setRankingConfig({...rankingConfig, loss12: parseInt(e.target.value) || 0})}
                          className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                        />
                      </div>
                      <div className="w-full text-center">
                        <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">P: 0-2</label>
                        <input
                          type="number"
                          value={rankingConfig.loss02}
                          onChange={(e) => setRankingConfig({...rankingConfig, loss02: parseInt(e.target.value) || 0})}
                          className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col 2xl:flex-row gap-3 items-end">
                  {/* Multiplicador de Racha */}
                  <div className="flex-1 bg-black/40 p-3 rounded-lg border border-white/5">
                    <span className="text-[9px] font-black uppercase tracking-widest text-amber-400 block text-center w-full mb-2">Racha Victorias (On Fire)</span>
                    <div className="grid grid-cols-2 gap-3 max-w-[200px] mx-auto md:mx-0">
                      <div className="w-full text-center">
                        <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">Partidos</label>
                        <input
                          type="number"
                          value={rankingConfig.streakThreshold}
                          onChange={(e) => setRankingConfig({...rankingConfig, streakThreshold: parseInt(e.target.value) || 0})}
                          className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-2 py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                        />
                      </div>
                      <div className="w-full text-center">
                        <label className="block text-[8px] font-black uppercase opacity-60 mb-1 text-white">Multip (X)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={rankingConfig.streakMultiplier}
                          onChange={(e) => setRankingConfig({...rankingConfig, streakMultiplier: parseFloat(e.target.value) || 0})}
                          className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-2 py-1.5 focus:border-amber-500 outline-none transition-all font-black text-center text-xs text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Save Button for Ranking */}
                  <div className="w-full md:w-auto mt-2 md:mt-0 md:self-stretch flex">
                    <button
                      type="submit"
                      disabled={savingRanking}
                      className="w-full bg-amber-500 text-black hover:bg-amber-400 font-black px-6 rounded-lg transition-all shadow-md hover:scale-[1.01] active:scale-95 flex items-center justify-center gap-2 uppercase tracking-wider text-[10px] cursor-pointer"
                    >
                      {savingRanking ? (
                        <div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                      ) : (
                        <>
                          <Save size={14} /> Guardar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Cierre de Registros (Pre-Lanzamiento) */}
            <div className="bg-gradient-to-br from-amber-950/40 to-black/60 p-4 rounded-xl border border-amber-500/20 shadow-xl relative overflow-hidden mb-8">
              <h3 className="text-sm font-black uppercase tracking-wider flex items-center gap-2 text-amber-400 mb-2">
                <Users size={18} />
                Fase de Pre-Lanzamiento (Registros)
              </h3>
              <p className="text-xs text-white/60 mb-4">
                Controla si los nuevos usuarios pueden registrarse o iniciar sesión. Ideal para crear expectativa antes de abrir la app al público general.
              </p>

              <div className="bg-black/40 p-3 rounded-xl border border-white/5 flex items-center justify-between gap-4">
                <label className="flex items-center gap-3 cursor-pointer w-full">
                  <div className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${form.registrationsEnabled !== false ? 'bg-amber-500' : 'bg-white/20'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.registrationsEnabled !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                  </div>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={form.registrationsEnabled !== false}
                    onChange={(e) => setForm({ ...form, registrationsEnabled: e.target.checked })}
                  />
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-white">
                      {form.registrationsEnabled !== false ? '✅ Registros Abiertos' : '🔒 Accesos Cerrados (Coming Soon)'}
                    </span>
                    <p className="text-[10px] text-white/50 leading-tight">
                      {form.registrationsEnabled !== false 
                        ? 'Cualquier persona puede ingresar y crearse una cuenta.' 
                        : 'Nadie puede entrar. Se mostrará el cartel de "Ya falta muy poco".'}
                    </p>
                  </div>
                </label>
              </div>
            </div>


            {/* Zona de Peligro */}
            <div className="bg-red-950/20 p-4 rounded-xl border border-red-500/30 shadow-xl relative overflow-hidden mt-8 md:col-span-2">
              <h3 className="text-sm font-black uppercase tracking-wider flex items-center gap-2 text-red-400 mb-2">
                Zona de Peligro (Pruebas)
              </h3>
              <p className="text-xs text-red-400/60 mb-4">
                Herramientas destructivas para reiniciar el sistema durante la etapa de desarrollo y pruebas.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4 flex-wrap">
                <button
                  onClick={handleDeleteAllTournaments}
                  className="w-full sm:w-auto bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 text-red-400 hover:text-red-300 font-black px-6 py-3 rounded-xl uppercase tracking-widest transition-all shadow-lg text-[10px] md:text-xs flex items-center justify-center"
                >
                  Vaciar Torneos & Partidos
                </button>
                <button
                  onClick={handleDeleteAllStats}
                  className="w-full sm:w-auto bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 text-red-400 hover:text-red-300 font-black px-6 py-3 rounded-xl uppercase tracking-widest transition-all shadow-lg text-[10px] md:text-xs flex items-center justify-center"
                >
                  Resetear TODO (Stats + Puntos)
                </button>
                <button
                  onClick={handleResetRankingOnly}
                  className="w-full sm:w-auto bg-orange-600/20 hover:bg-orange-600/40 border border-orange-500/50 text-orange-400 hover:text-orange-300 font-black px-6 py-3 rounded-xl uppercase tracking-widest transition-all shadow-lg text-[10px] md:text-xs flex items-center justify-center"
                >
                  Resetear Puntos de Ranking
                </button>
                <button
                  onClick={handleResetAchievements}
                  className="w-full sm:w-auto bg-yellow-600/20 hover:bg-yellow-600/40 border border-yellow-500/50 text-yellow-400 hover:text-yellow-300 font-black px-6 py-3 rounded-xl uppercase tracking-widest transition-all shadow-lg text-[10px] md:text-xs flex items-center justify-center"
                >
                  Resetear Logros (Trofeos)
                </button>
              </div>
            </div>

          </div>
        )}

      </div>

      <div className="flex flex-col items-end pt-4 pb-12 sticky bottom-4 z-50">
        <div className="bg-black/80 backdrop-blur-xl p-4 rounded-[var(--radius-large)] border border-white/10 shadow-2xl flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
          <div className="flex flex-col items-start gap-2 w-full md:w-auto">
            <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest hidden md:inline-block">Guardado Diferencial (Smart Save)</span>
            <button
              onClick={() => { setShowPresetsModal(true); loadPresets(); }}
              className="bg-white/5 hover:bg-white/10 text-white border border-white/10 font-bold px-6 py-3 rounded-xl text-xs uppercase tracking-widest transition-all w-full md:w-auto"
            >
              Presets guardados
            </button>
          </div>
          <button
            onClick={handleSave}
            className="theme-primary-bg text-black font-black px-8 py-3 md:px-10 md:py-4 rounded-xl text-sm md:text-lg uppercase tracking-widest hover:opacity-80 transition-all shadow-[0_0_20px_rgba(var(--glow-rgb), 0.3)] hover:scale-105 active:scale-95 flex items-center w-full md:w-auto justify-center"
          >
            <CheckCircle2 className="mr-2 md:mr-3" size={20} /> Guardar Cambios
          </button>
          {msg && <p className="font-black text-xs md:text-sm theme-primary-text animate-pulse absolute -top-8 right-4 bg-black/80 px-4 py-2 border border-white/10 rounded-full">{msg}</p>}
        </div>
      </div>

      {showPresetsModal && (
        <div className="fixed bottom-0 right-0 z-[100] p-4 md:p-8 flex flex-col items-end pointer-events-none w-full h-full justify-end">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl border border-white/20 rounded-[var(--radius-large)] w-full max-w-sm shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col max-h-[70vh] pointer-events-auto animate-in slide-in-from-bottom-8 fade-in flex-shrink-0 mb-[160px] md:mb-[120px]">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black leading-none">Presets</h3>
                <p className="text-[10px] opacity-60 mt-1 uppercase tracking-wider">Guardar/Cargar Configs</p>
              </div>
              <button
                onClick={() => setShowPresetsModal(false)}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-6 flex flex-col">
              <div className="bg-black/60 border border-white/10 rounded-2xl p-4 flex flex-col gap-3">
                <div className="w-full">
                  <input
                    type="text"
                    placeholder="Nombre del Preset (ej: Torneos)"
                    value={presetNameInput}
                    onChange={e => setPresetNameInput(e.target.value)}
                    className="w-full bg-white/5 text-white placeholder-white/30 border border-white/10 rounded-xl px-3 py-2 text-sm focus:border-white/30 outline-none transition-colors"
                  />
                </div>
                <button
                  onClick={handleSavePreset}
                  className="w-full bg-theme-primary text-black font-bold px-4 py-2 rounded-xl hover:opacity-80 transition-opacity text-sm flex items-center justify-center"
                >
                  <Settings size={14} className="inline mr-2" />
                  Guardar Actual
                </button>
              </div>

              <div>
                <h4 className="font-bold text-[10px] opacity-50 mb-3 uppercase tracking-widest text-[#eab308]">Mis Presets</h4>
                {loadingPresets ? (
                  <p className="text-center opacity-50 py-4 text-sm">Cargando...</p>
                ) : presets.length === 0 ? (
                  <p className="text-center opacity-50 py-4 text-sm">No hay presets creados</p>
                ) : (
                  <div className="space-y-2">
                    {presets.map(preset => (
                      <div key={preset.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex flex-col gap-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-bold text-sm text-white">{preset.presetName}</h5>
                            <span className="text-[10px] opacity-50 block mt-0.5">{new Date(preset.createdAt).toLocaleDateString()}</span>
                          </div>
                          <button
                            onClick={(e) => { e.preventDefault(); handleDeletePreset(preset.id); }}
                            className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white rounded-lg transition-colors cursor-pointer relative z-10"
                            title="Eliminar"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <button
                          onClick={() => handleApplyPreset(preset)}
                          className="w-full border border-white/10 bg-white/5 hover:bg-white/10 text-white font-bold px-3 py-1.5 rounded-lg transition-colors text-xs"
                        >
                          Aplicar Preset
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
